'use client';

import { Box, Card, Typography } from '@mui/material';
import Image from 'next/image';
import Link from 'next/link';

import { NewsArticle } from '../../../services/types/news';
import {
  getImageAlt,
  getImageUrl,
  hasValidImageUrl,
} from '../../../services/utils/image';

export interface NewsCardProps {
  article: NewsArticle;
  variant?: 'default' | 'featured' | 'compact';
}

const NewsCard = ({
  article,
  variant: _variant = 'default',
}: NewsCardProps) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Card
      component={Link}
      href={`/news/${article.slug}`}
      aria-label={`Read article: ${article.title}`}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        textDecoration: 'none',
        borderRadius: 0,
        boxShadow: 'none',
        border: 'none',
        backgroundColor: 'white',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      {/* Featured Image */}
      {article.featuredImage && hasValidImageUrl(article.featuredImage) ? (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: 259, // Fixed height
            overflow: 'hidden',
          }}
        >
          <Image
            src={getImageUrl(article.featuredImage)}
            alt={getImageAlt(article.featuredImage)}
            fill
            style={{
              objectFit: 'cover',
            }}
            sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
          />
        </Box>
      ) : (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: 259, // Fixed height
            backgroundColor: '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#999',
            fontSize: '14px',
          }}
        >
          No Image
        </Box>
      )}

      {/* Content */}
      <Box
        sx={{
          p: 4, // Increased padding
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
        }}
      >
        {/* Title */}
        <Typography
          variant='h6'
          component='h3'
          sx={{
            fontFamily: 'SF Pro, -apple-system, BlinkMacSystemFont, sans-serif',
            fontWeight: 510,
            fontSize: '16px',
            lineHeight: 1.5,
            color: '#1A1A1A',
            mb: 4, // Increased margin
            display: '-webkit-box',
            WebkitLineClamp: 2, // Fixed line clamp
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {article.title}
        </Typography>

        {/* Excerpt */}
        {article.excerpt && (
          <Typography
            variant='body2'
            sx={{
              fontFamily:
                'SF Pro, -apple-system, BlinkMacSystemFont, sans-serif',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: 1.428,
              color: '#4D4C4C',
              mb: 4, // Increased margin
              flexGrow: 1,
              display: '-webkit-box',
              WebkitLineClamp: 3, // Fixed line clamp
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textAlign: 'justify',
            }}
          >
            {article.excerpt}
          </Typography>
        )}

        {/* Divider */}
        <Box
          sx={{
            width: '100%',
            height: '1px',
            backgroundColor: '#E6E6E6',
            mb: 4, // Increased margin
          }}
        />

        {/* Meta Information */}
        <Typography
          variant='caption'
          sx={{
            fontFamily: 'SF Pro, -apple-system, BlinkMacSystemFont, sans-serif',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: 1.428,
            color: '#999999',
          }}
        >
          {article.category?.name} |{' '}
          {formatDate(article.publishedDate || article.createdAt)}
        </Typography>
      </Box>
    </Card>
  );
};

export default NewsCard;
